import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

// =============================================================================
// MODAL DE SELEÇÃO (múltipla) — pontos / regiões / publicadores
// - modo cliente: recebe `itens` (array) e faz busca/paginação local
// - modo servidor: recebe `fetchPage({ search, page, limit }) => { itens, total }`
// =============================================================================

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(30, 41, 59, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4000;
  padding: 16px;
`;

const Card = styled.div`
  background: #fff;
  width: 100%;
  max-width: 860px;
  max-height: 88vh;
  border-radius: 12px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Roboto', sans-serif;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #eef1f4;
  font-size: 13px;
  font-weight: 700;
  color: #475569;
`;

const HeadCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  &:nth-child(1), &:nth-child(2) { flex: 1; min-width: 0; }
`;

const SelTodos = styled.button`
  border: 1px solid #14b8a6;
  background: #fff;
  color: #0f766e;
  border-radius: 16px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: #f0fdfa; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const BtnFechar = styled.button`
  border: none;
  background: none;
  font-size: 20px;
  line-height: 1;
  color: #64748b;
  cursor: pointer;
  &:hover { color: #0f172a; }
`;

const Body = styled.div`
  overflow-y: auto;
  flex: 1;
  min-height: 120px;
`;

const Linha = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto 32px;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #334155;
`;

const Principal = styled.div`
  font-weight: 700;
  color: #1e293b;
  & small { display: block; font-weight: 400; color: #94a3b8; font-size: 12px; margin-top: 2px; }
`;

const BtnSelecionar = styled.button`
  border: none;
  border-radius: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$sel ? '#0f766e' : '#14b8a6')};
  color: #fff;
  white-space: nowrap;
  &:hover { background: #0f766e; }
`;

const Vazio = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #94a3b8;
  font-style: italic;
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 12px 20px;
  border-top: 1px solid #eef1f4;
  background: #fafcfd;
  font-size: 13px;
  color: #64748b;
`;

const Busca = styled.input`
  flex: 1;
  min-width: 160px;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 20px;
  font-size: 13px;
  outline: none;
  &:focus { border-color: #14b8a6; box-shadow: 0 0 0 2px rgba(20,184,166,0.15); }
`;

const MiniSelect = styled.select`
  padding: 6px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 13px;
`;

const Seta = styled.button`
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  color: #475569;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const Confirmar = styled.button`
  margin-left: auto;
  background: #008080;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 9px 22px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #006666; }
  &:disabled { background: #cbd5e1; cursor: not-allowed; }
`;

const casaBusca = (item, keys, termo) => {
  if (!termo) return true;
  const t = termo.trim().toLowerCase();
  return keys.some((k) => String(item?.[k] ?? '').toLowerCase().includes(t));
};

const ModalSelecao = ({
  titulo = 'Selecionar',
  colunas = [],
  itens = null,
  fetchPage = null,
  buscaKeys = ['nome'],
  selecionados = [],
  onConfirmar,
  onFechar,
}) => {
  const [busca, setBusca] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(8);
  const [sel, setSel] = useState(() => selecionados.slice());
  const [srvItens, setSrvItens] = useState([]);
  const [srvTotal, setSrvTotal] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const debounceRef = useRef(null);

  const modoServidor = typeof fetchPage === 'function';

  // ---- modo servidor: busca a página no backend ----
  useEffect(() => {
    if (!modoServidor) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setCarregando(true);
      try {
        const r = await fetchPage({ search: busca.trim(), page, limit });
        setSrvItens(Array.isArray(r?.itens) ? r.itens : []);
        setSrvTotal(typeof r?.total === 'number' ? r.total : 0);
      } finally {
        setCarregando(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [modoServidor, fetchPage, busca, page, limit]);

  // ---- modo cliente ----
  const clienteFiltrados = useMemo(() => {
    if (modoServidor) return [];
    return (itens || []).filter((i) => casaBusca(i, buscaKeys, busca));
  }, [modoServidor, itens, buscaKeys, busca]);

  const total = modoServidor ? srvTotal : clienteFiltrados.length;
  const totalPaginas = Math.max(1, Math.ceil(total / limit));
  const linhas = modoServidor
    ? srvItens
    : clienteFiltrados.slice((page - 1) * limit, page * limit);

  useEffect(() => { setPage(1); }, [busca, limit]);

  const estaSel = (item) => sel.some((s) => String(s._id) === String(item._id));
  const toggle = (item) => {
    setSel((prev) =>
      prev.some((s) => String(s._id) === String(item._id))
        ? prev.filter((s) => String(s._id) !== String(item._id))
        : [...prev, item]
    );
  };

  const unir = (base, novos) => {
    const map = new Map(base.map((s) => [String(s._id), s]));
    novos.forEach((i) => map.set(String(i._id), i));
    return Array.from(map.values());
  };

  // "Selecionar todos": no cliente, todos os itens filtrados; no servidor, busca a lista inteira
  const selecionarTodos = async () => {
    if (!modoServidor) {
      setSel((prev) => unir(prev, clienteFiltrados));
      return;
    }
    setCarregando(true);
    try {
      const r = await fetchPage({ search: busca.trim(), page: 1, limit: Math.max(total || 0, 1000) });
      const todos = Array.isArray(r?.itens) ? r.itens : [];
      setSel((prev) => unir(prev, todos));
    } finally {
      setCarregando(false);
    }
  };

  const inicio = total === 0 ? 0 : (page - 1) * limit + 1;
  const fim = Math.min(page * limit, total);

  return (
    <Overlay onMouseDown={(e) => { if (e.target === e.currentTarget) onFechar?.(); }}>
      <Card onMouseDown={(e) => e.stopPropagation()}>
        <Head>
          <HeadCell>{colunas[0]?.label || titulo}</HeadCell>
          <HeadCell>{colunas[1]?.label || ''}</HeadCell>
          <HeadCell>Selecione</HeadCell>
          <SelTodos type="button" onClick={selecionarTodos} disabled={carregando || total === 0}>
            Selecionar todos
          </SelTodos>
          <BtnFechar type="button" onClick={onFechar} aria-label="Fechar">×</BtnFechar>
        </Head>

        <Body>
          {carregando ? (
            <Vazio>Carregando…</Vazio>
          ) : linhas.length === 0 ? (
            <Vazio>Nenhum resultado.</Vazio>
          ) : (
            linhas.map((item) => (
              <Linha key={item._id}>
                <Principal>{colunas[0] ? colunas[0].render(item) : item.nome}</Principal>
                <div>{colunas[1] ? colunas[1].render(item) : ''}</div>
                <BtnSelecionar
                  type="button"
                  $sel={estaSel(item)}
                  onClick={() => toggle(item)}
                >
                  {estaSel(item) ? '✓ Selecionado' : 'Selecionar'}
                </BtnSelecionar>
                <span />
              </Linha>
            ))
          )}
        </Body>

        <Foot>
          <span>Mostrar linhas por página</span>
          <MiniSelect value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
            <option value={8}>8</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </MiniSelect>
          <Busca
            placeholder="Pesquisar"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <span>{inicio}-{fim} de {total}</span>
          <Seta type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</Seta>
          <Seta type="button" disabled={page >= totalPaginas} onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))}>›</Seta>
          <Confirmar
            type="button"
            disabled={sel.length === 0}
            onClick={() => { onConfirmar?.(sel); onFechar?.(); }}
          >
            Confirmar ({sel.length})
          </Confirmar>
        </Foot>
      </Card>
    </Overlay>
  );
};

export default ModalSelecao;
